import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams, useRouterState, useSearch } from '@tanstack/react-router';
import { ArrowLeft, Building2, Check, ExternalLink, Loader2, Plus, Search } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { CompanyDialog } from '../components/companies/company-dialog.tsx';
import { useDebounced } from '../components/record-table/use-debounced.ts';
import { StagePill } from '../components/ui/tone-pill.tsx';
import {
  AddToPipelineDialog,
  BulkPipelineBar,
  describeAddResult,
} from '../components/pipelines/add-to-pipeline-bulk.tsx';
import { ProjectDetail } from '../components/projects/project-detail.tsx';
import { PROJECT_CARD } from '../lib/card-fields.ts';
import { RecordList, type ListApi } from '../components/boards/record-list.tsx';
import { type ColumnDef } from '../components/record-table/index.ts';
import { Gate } from '../components/shell/gate.tsx';
import { Button } from '../components/ui/button.tsx';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog.tsx';
import { Input } from '../components/ui/input.tsx';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select.tsx';
import { describeError } from '../lib/api.ts';
import { api, type CompanyView, type ProjectView } from '../lib/client.ts';
import { cn } from '../lib/utils.ts';
import { useVisibleDefinitions } from '../lib/custom-fields.ts';
import { projectFilterFields } from '../lib/list-filters.ts';
import { useListView } from '../lib/list-view.ts';
import { joinProjectFilter, splitProjectFilter } from '../lib/project-filters.ts';
import { projectQuery, stageRefOf, useMembers, usePipelines } from '../lib/record-data.ts';
import { useSession } from '../lib/session.tsx';
import { PageHeader } from '../components/ui/page-header.tsx';

/**
 * Projects (RELAY-952): the list on RecordTable — server-paged newest first, the filter box
 * searching names — and the full project page. A project is created with its company here
 * (a project always has exactly one, searched for or created on the spot); everything after
 * happens on the project page.
 *
 * Both layouts show ONE pipeline (TS-1749), picked beside the toolbar and kept in the view
 * state, so a saved view or a link opens on it. The default pipeline lists every project (one
 * that left it has no stage there); any other lists only the projects in it. The board's
 * columns, the stage column and the stage filter are that pipeline's. Multi board shows them
 * all instead: a foldable lane per live pipeline, each its own board (`MultiBoard`).
 */
export function ProjectsPage() {
  const session = useSession();
  const navigate = useNavigate();
  const search = useSearch({ from: '/authed/projects' });
  const [creating, setCreating] = useState(false);
  const qc = useQueryClient();
  // Bulk: tick rows in the table, then add them to another pipeline. Stages are never edited
  // in bulk; the projects join the pipeline, and those already in it stay where they are.
  const canUpdate = session.can('customer.update');
  const [selected, setSelected] = useState<Set<string>>(() => new Set());
  const [adding, setAdding] = useState<string[] | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const pipelines = usePipelines('project');
  const { members } = useMembers();
  const definitions = useVisibleDefinitions('projects');
  const write = useCallback(
    (view: string) => void navigate({ to: '/projects', search: { view }, replace: true }),
    [navigate],
  );
  const { state, setState, activeViewId } = useListView({
    entity: 'projects',
    remember: 'projects',
    encoded: search.view,
    write,
  });
  const userId = session.me.user.id;
  const quick = useMemo(() => splitProjectFilter(state.filter, userId), [state.filter, userId]);
  const conditions = quick.conditions;
  const setQuick = (next: Partial<typeof quick>) =>
    setState({ ...state, filter: joinProjectFilter({ ...quick, ...next }, userId) });
  // A pipeline that is gone (archived, or a stale link) falls back to the default.
  const pipeline =
    pipelines.find((p) => p.id === state.pipelineId) ?? pipelines.find((p) => p.isDefault);
  const pipelineId = pipeline && !pipeline.isDefault ? pipeline.id : undefined;
  const listApi = useMemo(() => projectList(pipelineId), [pipelineId]);
  // Multi board: every pipeline's board stacked, each over its own scoped list.
  const lanes = useMemo(() => {
    const apis = new Map(
      pipelines.map((p) => [p.id, projectList(p.isDefault ? undefined : p.id)] as const),
    );
    return {
      pipelines,
      apiFor: (p: (typeof pipelines)[number]) => apis.get(p.id)!,
      noun: { one: 'project', many: 'projects' },
    };
  }, [pipelines]);
  const multi = state.layout === 'multiboard';
  const tableLayout = !state.layout || state.layout === 'table';
  // A different filter, search, pipeline or layout is a different set of rows: never carry a
  // selection across it.
  const selectionScope = JSON.stringify([
    state.filter ?? null,
    state.q ?? null,
    pipeline?.id,
    state.layout,
  ]);
  useEffect(() => setSelected(new Set()), [selectionScope]);
  const pickPipeline = (id: string) => {
    const next = pipelines.find((p) => p.id === id);
    // A stage filter names a stage of the pipeline it was set in: it goes with it.
    const kept = conditions.filter((c) => c.field !== 'stageId');
    setState({
      ...state,
      pipelineId: next && !next.isDefault ? next.id : undefined,
      filter: joinProjectFilter({ ...quick, conditions: kept }, userId),
    });
  };
  // Hints come from the picked pipeline's own search, as `listApi` is scoped to it.
  const filterFields = useMemo(
    () => projectFilterFields({ definitions, search: listApi.search, pipeline, members }),
    [definitions, listApi, pipeline, members],
  );
  const columns = useMemo<ColumnDef<ProjectView, unknown>[]>(
    () => [
      { accessorKey: 'name', header: 'Project', size: 260 },
      {
        id: 'company',
        header: 'Company',
        size: 200,
        enableSorting: false,
        cell: ({ row }) => row.original.company.name,
      },
      {
        id: 'primaryContact',
        header: 'Primary contact',
        size: 180,
        enableSorting: false,
        accessorFn: (p) => p.primaryContact?.name ?? '—',
      },
      {
        id: 'stage',
        header: pipeline?.name ?? 'Stage',
        size: 180,
        // Measured by the stage's name; the pill adds its padding.
        accessorFn: (p) => stageIn(p, pipelineId)?.name ?? '',
        meta: { autoSize: 24 },
        enableSorting: false,
        // The stage in the pipeline shown; a project that left the default has none there (TS-1747).
        cell: ({ row }) => {
          const stage = stageIn(row.original, pipelineId);
          if (!stage) return <span className="text-muted-foreground">—</span>;
          return <StagePill name={stage.name} terminalKind={stage.terminalKind} />;
        },
      },
      {
        accessorKey: 'createdAt',
        header: 'Created',
        size: 140,
        cell: ({ row }) => new Date(row.original.createdAt).toLocaleDateString(),
      },
    ],
    [pipeline, pipelineId],
  );
  return (
    <Gate permission="customer.read" feature="customers.enabled">
      <PageHeader
        title="Projects"
        description="Client engagements: one company, its contacts, deals and jobs."
        actions={
          <>
            {notice && (
              <span className="text-muted-foreground text-sm" role="status">
                {notice}
              </span>
            )}
            {session.can('customer.create') && (
              <Button onClick={() => setCreating(true)} data-testid="new-project">
                <Plus /> New project
              </Button>
            )}
          </>
        }
      />
      {canUpdate && tableLayout && selected.size > 0 && (
        <div className="mb-3">
          <BulkPipelineBar
            count={selected.size}
            onAdd={() => setAdding([...selected])}
            onClear={() => setSelected(new Set())}
          />
        </div>
      )}
      {pipeline ? (
        <RecordList
          key={pipeline.id}
          entity="projects"
          state={state}
          setState={setState}
          activeViewId={activeViewId}
          filter={state.filter}
          conditions={conditions}
          onConditionsChange={(next) => setQuick({ conditions: next })}
          filterFields={filterFields}
          quickFilters={
            <>
              <Button
                variant="outline"
                size="sm"
                aria-pressed={quick.mine}
                onClick={() => setQuick({ mine: !quick.mine })}
                className={cn(quick.mine && 'border-brand bg-brand/10 text-foreground')}
              >
                Mine
              </Button>
              {/* Multi board shows every pipeline already: no picker. */}
              {pipelines.length > 1 && !multi && (
                <Select value={pipeline.id} onValueChange={pickPipeline}>
                  <SelectTrigger
                    className="w-56"
                    aria-label="Pipeline"
                    data-testid="pipeline-picker"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {pipelines.map((p) => (
                      <SelectItem key={p.id} value={p.id}>
                        {p.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </>
          }
          api={listApi}
          columns={columns}
          pipeline={pipeline}
          lanes={lanes}
          cardSet={PROJECT_CARD}
          canMove={session.can('customer.update')}
          onOpen={(p) => void navigate({ to: '/projects/$id', params: { id: p.id } })}
          emptyMessage={quick.mine ? 'You own no projects.' : 'No projects yet.'}
          searchPlaceholder="Search projects, client names or emails…"
          {...(canUpdate ? { selection: selected, onSelectionChange: setSelected } : {})}
        />
      ) : (
        <p className="text-muted-foreground text-sm">Loading…</p>
      )}
      {adding !== null && (
        <AddToPipelineDialog
          noun="project"
          ids={adding}
          add={({ ids, ...rest }) => api.projects.addManyToPipeline({ ...rest, projectIds: ids })}
          pipelines={pipelines}
          current={pipeline?.id}
          onClose={() => setAdding(null)}
          onDone={async (result) => {
            setAdding(null);
            setSelected(new Set());
            setNotice(describeAddResult(result));
            await Promise.all(
              [['records'], ['board', 'projects'], ['project']].map((queryKey) =>
                qc.invalidateQueries({ queryKey }),
              ),
            );
          }}
        />
      )}
      {creating && (
        <NewProjectDialog
          onClose={() => setCreating(false)}
          onCreated={(p) => void navigate({ to: '/projects/$id', params: { id: p.id } })}
        />
      )}
    </Gate>
  );
}

/**
 * Projects as a list and board, in one pipeline (TS-1749): search and totals scoped to it, and a
 * move through the project PATCH, which moves only the place in the pipeline the stage belongs to.
 * Unset `pipelineId` is the default pipeline, the API's unscoped list.
 */
function projectList(pipelineId: string | undefined): ListApi<ProjectView> {
  const scoped = pipelineId ? { pipelineId } : {};
  return {
    search: (body) => api.projects.search({ ...body, ...scoped }),
    counts: (body) => api.projects.counts({ ...body, ...scoped }),
    move: (p, stageId) => api.projects.patch(p.id, { stageId }),
    idOf: (p) => p.id,
    // A project with no stage in this pipeline is in no column (the API never returns one).
    stageOf: (p) => stageIn(p, pipelineId)?.id ?? '',
    titleOf: (p) => p.name,
    moved: (p, stage) => {
      const ref = stageRefOf(stage);
      const target = pipelineId ?? p.pipelines.find((m) => m.isDefault)?.pipelineId;
      const places = p.pipelines.map((m) =>
        m.pipelineId === target ? { ...m, stageId: stage.id, stage: ref } : m,
      );
      const inDefault = places.find((m) => m.isDefault);
      return {
        ...p,
        pipelines: places,
        stageId: inDefault ? inDefault.stageId : p.stageId,
        stage: inDefault ? inDefault.stage : p.stage,
      };
    },
    sortable: ['name', 'createdAt', 'updatedAt'],
  };
}

/** A project's stage in the pipeline shown: the default's when none is picked. */
function stageIn(p: ProjectView, pipelineId: string | undefined) {
  if (!pipelineId) return p.stage;
  return p.pipelines.find((m) => m.pipelineId === pipelineId && !m.archivedAt)?.stage ?? null;
}

function NewProjectDialog({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (p: ProjectView) => void;
}) {
  const session = useSession();
  const [name, setName] = useState('');
  const [q, setQ] = useState('');
  const query = useDebounced(q.trim(), 250);
  const [company, setCompany] = useState<CompanyView | null>(null);
  const [creatingCompany, setCreatingCompany] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const companies = useQuery({
    queryKey: ['companies', query],
    queryFn: () => api.companies.list(query),
    enabled: query.length >= 3,
  });
  const create = useMutation({
    mutationFn: () => api.projects.create({ name: name.trim(), companyId: company!.id }),
    onSuccess: onCreated,
    onError: (err) => setError(describeError(err)),
  });
  const ready = name.trim() && company;
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>New project</DialogTitle>
        </DialogHeader>
        <form
          id="new-project-form"
          className="flex flex-col gap-3"
          onSubmit={(e) => {
            e.preventDefault();
            if (ready) create.mutate();
          }}
        >
          <label className="flex flex-col gap-1 text-sm">
            Project name
            <Input value={name} onChange={(e) => setName(e.target.value)} autoFocus />
          </label>
          <div className="flex flex-col gap-1 text-sm">
            Company
            <div className="flex items-center gap-2">
              <div className="relative flex-1">
                <Search className="text-muted-foreground pointer-events-none absolute top-1/2 left-3.5 size-4 -translate-y-1/2" />
                <Input
                  aria-label="Search companies"
                  placeholder="Search companies…"
                  value={q}
                  onChange={(e) => {
                    setQ(e.target.value);
                    setCompany(null);
                  }}
                  className="pl-10"
                />
              </div>
              {session.can('customer.create') && (
                <Button
                  type="button"
                  variant="outline"
                  size="icon-sm"
                  className="border-input bg-background shadow-xs"
                  aria-label="New company"
                  onClick={() => setCreatingCompany(true)}
                >
                  <Plus />
                </Button>
              )}
            </div>
            {query.length >= 3 && (
              <ul
                // 5.5 rows of 37px plus the border: the half row shows the list scrolls.
                className="border-border max-h-[205px] divide-y overflow-y-auto rounded-xl border"
                data-testid="company-picker"
              >
                {companies.isLoading && (
                  <li className="text-muted-foreground flex items-center gap-2 px-4 py-3 text-sm">
                    <Loader2 className="size-4 animate-spin" /> Searching…
                  </li>
                )}
                {companies.data && companies.data.items.length === 0 && (
                  <li className="text-muted-foreground px-4 py-3 text-center text-sm">
                    No companies match.
                  </li>
                )}
                {companies.data?.items.map((c) =>
                  c.projectId ? (
                    <li key={c.id} className="flex items-center gap-3 pr-4 text-sm">
                      <button
                        type="button"
                        disabled
                        className="flex min-w-0 flex-1 items-center gap-3 py-2 pl-4 text-left opacity-50"
                      >
                        <Building2 className="text-muted-foreground size-4 shrink-0" />
                        <span className="truncate font-medium">{c.name}</span>
                      </button>
                      <Link
                        to="/projects/$id"
                        params={{ id: c.projectId }}
                        target="_blank"
                        rel="noreferrer"
                        aria-label="Already has a project"
                        className="text-brand flex shrink-0 items-center gap-1 text-xs hover:underline"
                      >
                        Has a project <ExternalLink className="size-3" />
                      </Link>
                    </li>
                  ) : (
                    <li key={c.id}>
                      <button
                        type="button"
                        onClick={() => setCompany(c)}
                        className={cn(
                          'hover:bg-surface-hover flex w-full items-center gap-3 px-4 py-2 text-left text-sm transition-colors',
                          company?.id === c.id && 'bg-brand-soft/60',
                        )}
                        aria-pressed={company?.id === c.id}
                      >
                        <Building2 className="text-muted-foreground size-4 shrink-0" />
                        <span className="min-w-0 flex-1 truncate font-medium">{c.name}</span>
                        {company?.id === c.id && <Check className="text-brand size-4 shrink-0" />}
                      </button>
                    </li>
                  ),
                )}
              </ul>
            )}
            {company && query.length < 3 && (
              <p className="text-muted-foreground text-xs">Selected: {company.name}</p>
            )}
          </div>
          {error && (
            <p className="text-danger text-sm" role="alert">
              {error}
            </p>
          )}
        </form>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" form="new-project-form" disabled={!ready || create.isPending}>
            Create project
          </Button>
        </DialogFooter>
      </DialogContent>
      {creatingCompany && (
        <CompanyDialog
          onClose={() => setCreatingCompany(false)}
          onSaved={(c) => {
            setCreatingCompany(false);
            setCompany(c);
            setQ(c.name);
          }}
        />
      )}
    </Dialog>
  );
}

/** The full-page project route, `/projects/$id`. */
export function ProjectPage() {
  const { id } = useParams({ from: '/authed/projects/$id' });
  const { job: focusJobId, tab } = useSearch({ from: '/authed/projects/$id' });
  const conversation = useRouterState({ select: (s) => s.location.state.conversation });
  const navigate = useNavigate();
  const project = useQuery({ queryKey: projectQuery(id), queryFn: () => api.projects.get(id) });
  return (
    <Gate permission="customer.read" feature="customers.enabled">
      <div className="mb-4">
        <Button variant="ghost" size="sm" onClick={() => void navigate({ to: '/projects' })}>
          <ArrowLeft /> Projects
        </Button>
      </div>
      {project.isPending && (
        <div className="text-muted-foreground flex items-center gap-2 text-sm">
          <Loader2 className="size-4 animate-spin" /> Loading…
        </div>
      )}
      {project.error != null && (
        <p className="text-danger text-sm" role="alert">
          {describeError(project.error)}
        </p>
      )}
      {project.data && (
        <ProjectDetail
          project={project.data}
          focusJobId={focusJobId}
          initialTab={tab}
          openConversation={conversation}
        />
      )}
    </Gate>
  );
}
